/**
 * Gate que redirige a `/onboarding` si el usuario aún no completó el wizard,
 * o muestra la pantalla de pausa si el tenant no está `active`.
 *
 * Se usa en el árbol de rutas protegidas que requieren muni activo
 * (Radar, Expedientes, etc.). Convive con `ProtectedRoute` — ese se
 * encarga de la autenticación; este del contexto municipal.
 *
 * Mientras el contexto carga por primera vez muestra un spinner para evitar
 * parpadeos (render → redirect → re-render).
 *
 * Orden de chequeos (Sprint 25-2, Ruling RF-1): staff → pausa del tenant →
 * needsOnboarding → aceptación legal pendiente (sep 2026). La pausa va
 * ANTES que needsOnboarding a propósito: un invitado que nunca completó el
 * wizard de un tenant pausado debe ver la pantalla de pausa, no el
 * wizard — el wizard no tiene nada que ofrecerle
 * si el tenant no puede operar (el brief original la ponía después; esta
 * es la corrección vinculante).
 */
import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";

import { LOGIN_ACCESO_RETIRADO } from "@/lib/acceso-retirado";
import { useMunicipality } from "@/lib/municipality";
import { TenantPausado } from "@/pages/TenantPausado";

export function OnboardingGate({ children }: { children: ReactNode }) {
  const { needsOnboarding, isLoading, daneCode, context, isStaff } = useMunicipality();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <p className="text-sm text-slate-500">Cargando…</p>
      </div>
    );
  }

  // 7-sep-2026: lo quitaron del equipo (sin membership activa, con alguna
  // revocada). Ni shell ni wizard: a /login, que cierra la sesión y explica
  // el motivo. Va antes que todo lo demás — sin tenant, `needsOnboarding`
  // lo mandaría al wizard de municipio, que es justo lo que no debe ver.
  if (context?.access_revoked) {
    return <Navigate to={LOGIN_ACCESO_RETIRADO} replace />;
  }

  // Staff puro (ADR-016): is_staff=true sin tenant. Su landing es
  // /admin, no el wizard. Si el router lo manda a una ruta tenant-scope
  // sin tenant activo, lo redirigimos a /admin.
  if (isStaff && (!context?.has_tenant || !daneCode)) {
    return <Navigate to="/admin" replace />;
  }

  // Ruling RF-1: pantalla completa de pausa, sin AppShell — antes del
  // chequeo de needsOnboarding (ver docstring del módulo).
  if (context?.tenant_status && context.tenant_status !== "active") {
    return <TenantPausado context={context} />;
  }

  // `needsOnboarding` es true si no hay tenant o no hay `onboarded_at`.
  // Adicionalmente, si por alguna razón hay tenant pero el contexto quedó
  // sin DANE, también mandamos al wizard (defensivo).
  if (needsOnboarding || (context && !daneCode)) {
    return <Navigate to="/onboarding" replace />;
  }

  // Sep 2026: ya terminó el wizard pero no aceptó la versión vigente de los
  // términos o la política (cambio de fondo, o aceptó antes de que existiera
  // el registro). Va después de needsOnboarding: el usuario nuevo acepta en
  // el último paso del wizard, no en la pantalla de actualización. El
  // backend deja `legal_pending` vacío para el staff.
  if (context?.legal_pending?.length) {
    return <Navigate to="/actualizacion-legal" replace />;
  }

  return <>{children}</>;
}
